import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { logAppError } from "@/lib/appLogger";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
  componentStack?: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught error:", error.message, error.stack);
    console.error("Component stack:", errorInfo.componentStack);
    logAppError("react_error_boundary", error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });
    this.setState({ componentStack: errorInfo.componentStack ?? undefined });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen-safe flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-sm">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              The app encountered an unexpected error. Please try reloading.
            </p>
            {this.state.errorMessage && (
              <p className="text-xs text-destructive bg-destructive/10 rounded p-2 mb-2 text-left font-mono break-all">
                {this.state.errorMessage}
              </p>
            )}
            {this.state.componentStack && (
              <pre className="text-xs text-muted-foreground bg-muted rounded p-2 mb-4 text-left overflow-auto max-h-32">
                {this.state.componentStack.slice(0, 800)}
              </pre>
            )}
            <Button onClick={this.handleReload} size="lg" className="w-full">
              Reload App
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
